import { useState, useCallback, useEffect, useRef } from "react";
import toast from "react-hot-toast";
import axios from "axios";
import { sendVerificationCodeApi, verifyActivationApi } from "@/modules/auth/services/auth.api";
import { useLogout } from "@/modules/auth/hooks/useLogout";
import { notifyUserChanged } from "@/modules/auth/utils/authToken";
import { useCurrentUser } from "@/modules/auth/context/CurrentUserContext";
import { resolvePrimaryAccount } from "@/modules/auth/utils/userAccess";
import type { AccountType } from "@/modules/auth/types";

export function useEmailVerification(
  email: string | undefined,
  initialCodeSent = false,
  onSuccess?: () => void
) {
  const [sendingCode, setSendingCode] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [otp, setOtp] = useState("");
  const [timer, setTimer] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const { handleLogout } = useLogout();
  const { user, refetchUser } = useCurrentUser();
  // The same email can back several accounts (student/teacher/admin) - this
  // modal always acts on the resolved primary account (see resolvePrimaryAccount),
  // so its code must target that exact account_type, never guessed server-side.
  const accountType = resolvePrimaryAccount(user)?.account_type as AccountType | undefined;

  const startTimer = useCallback((seconds = 60) => {
    setTimer(seconds);
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setTimer((prev) => {
        if (prev <= 1) {
          if (timerRef.current) clearInterval(timerRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, []);

  useEffect(() => {
    if (initialCodeSent) {
      startTimer(60);
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [initialCodeSent, startTimer]);

  const sendCode = useCallback(async () => {
    if (!email || sendingCode || timer > 0) return;
    setSendingCode(true);
    setError(null);

    try {
      await sendVerificationCodeApi({ email, type: "activation", account_type: accountType });
      startTimer(60);
      toast.success("Verification code sent to your email.");
    } catch (err: unknown) {
      if (axios.isAxiosError(err) && err.response?.data) {
        const message = err.response.data.message;
        if (err.response.status === 429 || message === "too_many_requests") {
          const retryAfter = err.response.data.retry_after || 60;
          startTimer(retryAfter);
          setError(`Too many attempts. Please try again in ${retryAfter}s.`);
        } else if (message === "resend_too_soon") {
          const retryAfter = err.response.data.retry_after || 60;
          startTimer(retryAfter);
          setError(`Resend request too soon. Please wait ${retryAfter}s.`);
        } else {
          setError(err.response.data.message || "Failed to send verification code.");
        }
      } else {
        setError("Network error. Please check your connection.");
      }
    } finally {
      setSendingCode(false);
    }
  }, [email, sendingCode, timer, startTimer, accountType]);

  const verifyCode = useCallback(async (codeToVerify?: string) => {
    const code = codeToVerify ?? otp;
    if (!email || !code || code.length !== 6 || verifying) return;

    setVerifying(true);
    setError(null);

    try {
      await verifyActivationApi({ email, code, account_type: accountType });
      setSuccess(true);
      toast.success("Account verified successfully!");
      // Notify application & refetch user state
      notifyUserChanged();
      if (refetchUser) {
        await refetchUser();
      }
      if (onSuccess) {
        onSuccess();
      }
    } catch (err: unknown) {
      if (axios.isAxiosError(err) && err.response?.data) {
        const message = err.response.data.message;
        if (message === "invalid_code") {
          setError("Invalid verification code.");
        } else if (message === "expired_code") {
          setError("Expired verification code.");
        } else if (err.response.status === 429 || message === "too_many_requests") {
          setError("Too many attempts.");
        } else {
          setError(err.response.data.message || "Verification failed.");
        }
      } else {
        setError("Network error. Please check your connection.");
      }
    } finally {
      setVerifying(false);
    }
  }, [email, otp, verifying, refetchUser, accountType]);

  return {
    sendingCode,
    verifying,
    otp,
    setOtp,
    timer,
    canResend: timer === 0 && !sendingCode,
    error,
    setError,
    success,
    sendCode,
    verifyCode,
    logout: handleLogout,
  };
}
